/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useParams, useSearchParams } from 'react-router-dom';
import { LoadingScreen } from 'components/LoadingScreen';
import classes from '../modulecss/trackingPortalPage.module.css';
import { TrackingPageView } from '../tracking/TrackingPageView';
import { SearchScreenView } from '../tracking/SearchScreenView';
import { TrackingPageConfig, TrackingPageData } from '../tracking/types';

import { GET_TRACKING_PAGE_DATA, GET_STORE_SETTINGS } from 'gql/queries';

/** Hostnames that are ShipAid's own, not a merchant's custom subdomain. */
const RESERVED_SUBDOMAINS = ['track', 'staging-track-shipaid', 'localhost'];

function currentSubdomain(): string {
  return (
    window?.location?.hostname
      ?.split('.')
      .filter((part) => part !== 'www')
      .shift() ?? ''
  );
}

export function Home() {
  const params = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [trackingOrOrderNumber, setTrackingOrOrderNumber] = useState('');
  const [storeSettings, setStoreSettings] = useState<any | null>(null);
  const [data, setData] = useState<TrackingPageData | null>(null);
  const [errors, setErrors] = useState<string | null>(null);

  const stopLoading = () => {
    setLoading(false);
    document.body.classList.remove(classes.pageBody);
  };

  const [getTrackingPageData] = useLazyQuery(GET_TRACKING_PAGE_DATA, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      const page = result?.getTrackingPageData;
      if (page?.shipments?.length) {
        setErrors(null);
        setData(page);
      } else {
        setErrors('No tracking details found. Please try later.');
      }
      stopLoading();
    },
    onError(error) {
      setErrors(
        error?.message ?? 'No tracking details found. Please try later.',
      );
      stopLoading();
    },
  });

  const [getStoreSettings] = useLazyQuery(GET_STORE_SETTINGS, {
    onError() {
      setLoading(false);
    },
    onCompleted(result) {
      if (result?.storesSettings?.length)
        setStoreSettings(result.storesSettings[0]);
      setLoading(false);
    },
  });

  const lookup = (value: string) => {
    if (!value) return;
    setLoading(true);
    document.body.classList.add(classes.pageBody);
    getTrackingPageData({
      variables: { input: { emailOrPhone, trackingOrOrderNumber: value } },
    });
  };

  useEffect(() => {
    if (params?.id) lookup(params.id);
  }, [params.id]);

  // Branding for the search screen comes from the merchant's custom subdomain.
  // This runs regardless of whether the URL carries a tracking number, because a
  // failed lookup on a branded domain still has to render in that merchant's
  // theme rather than ShipAid's defaults.
  useEffect(() => {
    const subdomain = currentSubdomain();

    // ShipAid's own hosts have no merchant context, so `?store=` lets us preview
    // a merchant's branding there — which is the only way to see it on
    // localhost, where the hostname is always reserved.
    const override = searchParams.get('store');
    const customDomain = RESERVED_SUBDOMAINS.includes(subdomain)
      ? override
      : subdomain;

    if (customDomain) getStoreSettings({ variables: { customDomain } });
  }, []);

  if (loading) return <LoadingScreen />;

  if (data) return <TrackingPageView data={data} />;

  const searchConfig: TrackingPageConfig | undefined =
    storeSettings?.trackingPageConfig;

  return (
    <SearchScreenView
      config={searchConfig}
      legacyCoverImageUrl={storeSettings?.trackingPortalCoverImage ?? null}
      customDomain={storeSettings?.customDomain ?? null}
      emailOrPhone={emailOrPhone}
      trackingOrOrderNumber={trackingOrOrderNumber}
      onEmailOrPhoneChange={setEmailOrPhone}
      onTrackingOrOrderNumberChange={setTrackingOrOrderNumber}
      onSubmit={() => lookup(trackingOrOrderNumber)}
      error={errors}
    />
  );
}
